import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * Sheet — drawer latéral véritable (slide-only, pas de fade ni d'arrondi).
 *
 * Construit sur le primitive Radix Dialog (pour le focus trap, l'accessibilité,
 * Escape-to-close, le portal). L'animation est gérée par des keyframes CSS
 * définies dans `src/styles/globals.css` (cf. `drawer-slide-in-right`).
 *
 * Side variants : `right` (par défaut), `left`. Le drawer occupe toute la
 * hauteur de l'écran avec des coins droits.
 */
const Sheet = DialogPrimitive.Root
const SheetTrigger = DialogPrimitive.Trigger
const SheetPortal = DialogPrimitive.Portal
const SheetClose = DialogPrimitive.Close

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "drawer-overlay fixed inset-0 z-50 bg-slate-950/30",
      className
    )}
    {...props}
  />
))
SheetOverlay.displayName = "SheetOverlay"

type Side = "left" | "right"

const SIDE_POSITION: Record<Side, string> = {
  left: "inset-y-0 left-0 border-r",
  right: "inset-y-0 right-0 border-l",
}

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: Side
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, side = "right", children, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      data-side={side}
      className={cn(
        "drawer-content",
        // Position : full-height ancré sur le bord, coins droits
        "fixed z-50 flex flex-col h-screen bg-white dark:bg-slate-900 shadow-2xl",
        "border-gray-200 dark:border-slate-700",
        // Largeur par défaut, override via className depuis l'appel
        "w-full sm:w-[420px]",
        // Pas d'arrondi, formellement
        "rounded-none",
        SIDE_POSITION[side],
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-60 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2">
        <X className="h-4 w-4" />
        <span className="sr-only">Fermer</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = "SheetContent"

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1 border-b border-gray-100 dark:border-slate-700 px-6 py-4 shrink-0",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex-1 overflow-y-auto px-6 py-4", className)}
    {...props}
  />
)
SheetBody.displayName = "SheetBody"

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 border-t border-gray-100 dark:border-slate-700 px-6 py-3 shrink-0",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-base font-semibold tracking-tight", className)}
    style={{ textTransform: "none" }}
    {...props}
  />
))
SheetTitle.displayName = "SheetTitle"

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  // NB: on évite `text-sm text-gray-500` car les styles globaux (themes.css)
  // forcent uppercase + lettre-spacing + petite typo sur ces classes.
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm leading-relaxed", className)}
    style={{
      color: "#475569",
      textTransform: "none",
      letterSpacing: "normal",
      fontSize: "0.8125rem",
      fontWeight: 400,
      display: "block",
      marginBottom: 0,
    }}
    {...props}
  />
))
SheetDescription.displayName = "SheetDescription"

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
